import { z } from "zod";

export const usernameSchema = z
  .string()
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username must be at most 30 characters")
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    "Username can only contain letters, numbers, underscores, and hyphens"
  );

export const signUpSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  username: usernameSchema,
  displayName: z.string().min(1, "Display name is required").max(50),
});

export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export const profileSchema = z.object({
  displayName: z.string().min(1, "Display name is required").max(50),
  bio: z.string().max(300, "Bio must be at most 300 characters").optional(),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  twitter: z.string().max(15).optional().or(z.literal("")),
  github: z.string().max(39).optional().or(z.literal("")),
  linkedin: z.string().max(100).optional().or(z.literal("")),
});

export const postSchema = z.object({
  title: z
    .string()
    .min(5, "Title must be at least 5 characters")
    .max(100, "Title must be at most 100 characters"),
  subtitle: z.string().max(200, "Subtitle must be at most 200 characters").optional(),
  content: z.string().min(100, "Content must be at least 100 characters"),
  coverImage: z.string().url("Invalid URL").optional().or(z.literal("")),
  tags: z.array(z.string()).max(5, "Maximum 5 tags allowed"),
  category: z.string().min(1, "Category is required"),
});

export const commentSchema = z.object({
  content: z
    .string()
    .min(1, "Comment cannot be empty")
    .max(5000, "Comment must be at most 5000 characters"),
});

export const reportSchema = z.object({
  reason: z.enum(["spam", "harassment", "misinformation", "off-topic", "other"]),
  description: z.string().max(500, "Description must be at most 500 characters").optional(),
});

export const jobSchema = z.object({
  title: z
    .string()
    .min(5, "Title must be at least 5 characters")
    .max(100, "Title must be at most 100 characters"),
  companyName: z
    .string()
    .min(2, "Company name is required")
    .max(100, "Company name must be at most 100 characters"),
  description: z
    .string()
    .min(50, "Description must be at least 50 characters")
    .max(5000, "Description must be at most 5000 characters"),
  jobType: z.enum(["full-time", "part-time", "contract", "freelance"]),
  location: z
    .string()
    .min(2, "Location is required")
    .max(100, "Location must be at most 100 characters"),
  isRemote: z.boolean(),
  applyUrl: z.string().url("Must be a valid URL"),
  salaryMin: z.number().int().positive().optional().nullable(),
  salaryMax: z.number().int().positive().optional().nullable(),
});

export type SignUpFormData = z.infer<typeof signUpSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ProfileFormData = z.infer<typeof profileSchema>;
export type PostFormData = z.infer<typeof postSchema>;
export type CommentFormData = z.infer<typeof commentSchema>;
export type ReportFormData = z.infer<typeof reportSchema>;
export type JobFormData = z.infer<typeof jobSchema>;

